import { z, ZodError } from 'zod'
import { oloApi } from '@/lib/apiClient'
import { oloUrls } from '@/lib/urlBuilder'
import { normalizeZodError } from '@/lib/apiErrors'

// Mutually recursive types require z.lazy() + explicit ZodType<T>

export interface ReorderModifier {
	ref: string
	name: string
	modifierSets: ReorderModifierSet[]
}

export interface ReorderModifierSet {
	ref: string
	name: string
	modifiers: ReorderModifier[]
}

export const ReorderModifierSchema: z.ZodType<ReorderModifier> = z.lazy(() =>
	z.object({
		ref: z.string(),
		name: z.string(),
		modifierSets: z.array(ReorderModifierSetSchema),
	}),
)

export const ReorderModifierSetSchema: z.ZodType<ReorderModifierSet> = z.lazy(() =>
	z.object({
		ref: z.string(),
		name: z.string(),
		modifiers: z.array(ReorderModifierSchema),
	}),
)

export const PastOrderItemSchema = z.object({
	pastOrderItemGuid: z.string(),
	ref: z.string(),
	name: z.string(),
	description: z.string().nullable(),
	imageUrl800x800: z.string().nullable(),
	price: z.number(),
	totalPrice: z.number(),
	lastOrderedDate: z.string(),
	lastOrderModifiers: z.string().nullable(),
	quantity: z.number(),
	instructions: z.string().nullable(),
	stockStatus: z.enum(['OUT_OF_STOCK', 'LOW_STOCK']).nullable(),
	modifierSets: z.array(ReorderModifierSetSchema),
})
export type PastOrderItem = z.infer<typeof PastOrderItemSchema>

export const OrderAgainResponseSchema = z.object({
	page: z.number(),
	pageSize: z.number(),
	totalPages: z.number(),
	totalItems: z.number(),
	pastOrderItems: z.array(PastOrderItemSchema),
})
export type OrderAgainResponse = z.infer<typeof OrderAgainResponseSchema>

// The API may return multiple past items with identical modifier/modifierSet refs.
// Appending |{pastOrderItemGuid} makes every ref unique so items can be safely keyed by ref.

export function appendGuidToRefs(
	modifierSets: ReorderModifierSet[],
	guid: string,
): ReorderModifierSet[] {
	return modifierSets.map((ms) => ({
		...ms,
		ref: `${ms.ref}|${guid}`,
		modifiers: ms.modifiers.map((mod) => ({
			...mod,
			ref: `${mod.ref}|${guid}`,
			modifierSets: mod.modifierSets.length
				? appendGuidToRefs(mod.modifierSets, guid)
				: mod.modifierSets,
		})),
	}))
}

export function getOrderAgain(
	ref: string,
	scheduleRef: string,
	page: number,
	isDineIn: boolean,
): Promise<OrderAgainResponse> {
	let url = `${oloUrls.pastOrderItems(ref, scheduleRef)}?pageSize=20&page=${page}`
	if (isDineIn) url += '&dineIn=true'

	return oloApi
		.get<unknown>(url)
		.then((raw) => {
			const parsed = OrderAgainResponseSchema.parse(raw)
			return {
				...parsed,
				pastOrderItems: parsed.pastOrderItems.map((item) => ({
					...item,
					modifierSets: item.modifierSets.length
						? appendGuidToRefs(item.modifierSets, item.pastOrderItemGuid)
						: item.modifierSets,
				})),
			}
		})
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}
